import { useCallback, useEffect, useState } from "react";
import { isIOSSafari, isStandalone } from "../lib/pwa.js";

/**
 * Eventos de instalación ya consumidos.
 *
 * El hook tiene más de un consumidor vivo a la vez (el banner global de
 * `InstallPrompt` y el panel de sonido de las notificaciones), y `Chrome` emite
 * UN solo `BeforeInstallPromptEvent` que todos capturan: es el mismo objeto en
 * todas las instancias. Como `prompt()` solo se puede llamar una vez por
 * evento, "ya se usó" tiene que ser un hecho del evento y no del componente —
 * si no, consumirlo desde una instancia deja a las otras con un evento muerto
 * que al usarse lanza InvalidStateError y deja la promesa sin capturar.
 *
 * WeakSet y no Set: la clave es el evento, que muere con la página.
 */
const consumedEvents = new WeakSet();

/**
 * useInstallPrompt — Estado del flujo "instalar app".
 *
 * Chrome/Android dispara `beforeinstallprompt` cuando la PWA cumple los
 * criterios (manifest + service worker con fetch + HTTPS). Se intercepta para
 * disparar el diálogo desde nuestro propio botón en vez de dejar el mini-infobar
 * del navegador.
 *
 * iOS no tiene API equivalente: la instalación es manual desde el menú
 * Compartir, así que ahí solo se puede mostrar un instructivo (`needsIOSHint`).
 *
 * @returns {{
 *   canPrompt: boolean,      // hay prompt nativo disponible (Android)
 *   needsIOSHint: boolean,   // iOS Safari sin instalar → mostrar instructivo
 *   installed: boolean,      // ya corre en modo standalone
 *   promptInstall: () => Promise<"accepted"|"dismissed"|"unavailable">,
 * }}
 */
export function useInstallPrompt() {
  const [deferredEvent, setDeferredEvent] = useState(null);
  const [installed, setInstalled] = useState(() => isStandalone());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    function handleBeforeInstallPrompt(event) {
      // Sin preventDefault, Chrome muestra su propio infobar y el evento se
      // consume: perderíamos la posibilidad de pedirlo desde nuestra UI.
      event.preventDefault();
      setDeferredEvent(event);
    }

    function handleAppInstalled() {
      setInstalled(true);
      setDeferredEvent(null);
    }

    // El evento `change` de display-mode se dispara en ambos sentidos; solo
    // entrar en standalone significa "ya está instalada".
    function handleDisplayModeChange(event) {
      if (event.matches) handleAppInstalled();
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    // El usuario puede pasar a standalone sin recargar (Android, tras instalar).
    let media;
    try {
      media = window.matchMedia?.("(display-mode: standalone)");
      media?.addEventListener?.("change", handleDisplayModeChange);
    } catch {
      media = undefined;
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
      media?.removeEventListener?.("change", handleDisplayModeChange);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredEvent) return "unavailable";
    // Otra instancia del hook ya lo gastó: soltarlo en silencio en vez de
    // llamar a prompt() por segunda vez, que lanzaría.
    if (consumedEvents.has(deferredEvent)) {
      setDeferredEvent(null);
      return "unavailable";
    }
    consumedEvents.add(deferredEvent);
    try {
      deferredEvent.prompt();
      const { outcome } = await deferredEvent.userChoice;
      return outcome;
    } catch {
      // Chrome también lanza si el diálogo no se puede mostrar. Con ESTE evento
      // no hay nada que reintentar; emite uno nuevo si la app sigue elegible.
      return "unavailable";
    } finally {
      // El evento es de un solo uso: Chrome lo vuelve a emitir si sigue elegible.
      setDeferredEvent(null);
    }
  }, [deferredEvent]);

  return {
    canPrompt: deferredEvent !== null && !installed,
    needsIOSHint: !installed && isIOSSafari(),
    installed,
    promptInstall,
  };
}
